import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { Close, Search, Link as LinkIcon } from "@mui/icons-material";
import { useState, useEffect, useCallback } from "react";
import {
  buscarTarefasParaVinculo,
  criarVinculo,
  ICriarVinculoRequest,
  ITarefaResumoVinculo,
} from "services/modules/tarefaDetalhe";
import { useNavigation } from "contexts/notificationsContext";

interface ModalVincularTarefaProps {
  isOpen: boolean;
  onClose: () => void;
  idTarefaPrincipal: number;
  onTarefaVinculada: () => void;
}

export const ModalVincularTarefa = ({
  isOpen,
  onClose,
  idTarefaPrincipal,
  onTarefaVinculada,
}: ModalVincularTarefaProps) => {
  const { openNotification } = useNavigation();
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState<ITarefaResumoVinculo[]>([]);
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [selectedTarefa, setSelectedTarefa] = useState<ITarefaResumoVinculo | null>(null);
  const [tipoVinculo, setTipoVinculo] = useState<string>("relacionada");

  // Tipos de vínculo disponíveis
  const tiposVinculo = [
    { value: "relacionada", label: "Relacionada" },
    { value: "bloqueada_por", label: "Bloqueada por" },
    { value: "bloqueia", label: "Bloqueia" },
    { value: "duplicada", label: "Duplicada" },
    { value: "subtarefa_de", label: "Subtarefa de" },
  ];

  // Reset ao abrir o modal
  useEffect(() => {
    if (isOpen) {
      setSearchTerm("");
      setSearchResults([]);
      setSelectedTarefa(null);
      setTipoVinculo("relacionada");
    }
  }, [isOpen]);

  // Buscar tarefas
  const handleSearch = useCallback(async () => {
    if (!searchTerm || searchTerm.length < 2) {
      setSearchResults([]);
      return;
    }

    setSearching(true);
    try {
      const result = await buscarTarefasParaVinculo(searchTerm, idTarefaPrincipal);
      setSearchResults(result);
    } catch (error) {
      console.error("Erro ao buscar tarefas:", error);
    } finally {
      setSearching(false);
    }
  }, [searchTerm, idTarefaPrincipal]);

  // Debounce na busca
  useEffect(() => {
    const timer = setTimeout(() => {
      handleSearch();
    }, 500);

    return () => clearTimeout(timer);
  }, [handleSearch]);

  // Selecionar tarefa
  const handleSelectTarefa = (tarefa: ITarefaResumoVinculo) => {
    setSelectedTarefa(tarefa);
    setSearchTerm("");
    setSearchResults([]);
  };

  // Remover seleção
  const handleRemoveSelection = () => {
    setSelectedTarefa(null);
  };

  // Vincular tarefa
  const handleVincular = async () => {
    if (!selectedTarefa) {
      openNotification({
        isOpen: true,
        message: "Selecione uma tarefa para vincular.",
        type: "warning",
      });
      return;
    }

    setLoading(true);
    try {
      const request: ICriarVinculoRequest = {
        idTarefaPrincipal: idTarefaPrincipal,
        idTarefaVinculada: selectedTarefa.idTarefa,
        tipoVinculo: tipoVinculo,
      };

      const result = await criarVinculo(request);

      if (result) {
        openNotification({
          isOpen: true,
          message: `Tarefa #${selectedTarefa.numero} vinculada com sucesso!`,
          type: "success",
        });
        onTarefaVinculada();
        onClose();
      }
    } catch (error) {
      console.error("Erro ao vincular tarefa:", error);
      openNotification({
        isOpen: true,
        message: "Não foi possível vincular a tarefa.",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <LinkIcon color="primary" />
        Vincular Tarefa
        <IconButton
          aria-label="fechar"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {/* Tarefa Selecionada */}
        {selectedTarefa && (
          <Box mb={2}>
            <Typography variant="caption" color="textSecondary">
              Tarefa selecionada:
            </Typography>
            <Chip
              label={`#${selectedTarefa.numero} - ${selectedTarefa.titulo}`}
              onDelete={handleRemoveSelection}
              color="primary"
              variant="outlined"
              sx={{ mt: 0.5, maxWidth: "100%" }}
            />
          </Box>
        )}

        {/* Campo de Busca */}
        {!selectedTarefa && (
          <>
            <TextField
              autoFocus
              margin="dense"
              label="Buscar tarefa por número ou título"
              type="text"
              fullWidth
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Digite pelo menos 2 caracteres..."
              InputProps={{
                startAdornment: <Search sx={{ color: "action.active", mr: 1 }} />,
                endAdornment: searching && <CircularProgress size={20} />,
              }}
              sx={{ mb: 2 }}
            />

            {/* Resultados da Busca */}
            {searchResults.length > 0 && (
              <Box
                sx={{
                  maxHeight: 250,
                  overflow: "auto",
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                }}
              >
                <List dense>
                  {searchResults.map((tarefa) => (
                    <ListItem
                      key={tarefa.idTarefa}
                      button
                      onClick={() => handleSelectTarefa(tarefa)}
                      sx={{
                        "&:hover": {
                          backgroundColor: "action.hover",
                        },
                      }}
                    >
                      <ListItemText
                        primary={
                          <Box display="flex" alignItems="center" gap={1}>
                            <Typography variant="body2" fontWeight="bold">
                              #{tarefa.numero}
                            </Typography>
                            <Typography variant="body2" noWrap>
                              {tarefa.titulo}
                            </Typography>
                          </Box>
                        }
                        secondary={
                          <Box display="flex" gap={1} mt={0.5}>
                            <Chip
                              label={tarefa.nomeReferencia}
                              size="small"
                              variant="outlined"
                            />
                            <Chip
                              label={tarefa.nomeStatus}
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}

            {/* Mensagem quando não há resultados */}
            {searchTerm.length >= 2 && !searching && searchResults.length === 0 && (
              <Typography variant="body2" color="textSecondary" align="center" py={2}>
                Nenhuma tarefa encontrada.
              </Typography>
            )}

            {/* Mensagem inicial */}
            {searchTerm.length < 2 && (
              <Typography variant="body2" color="textSecondary" align="center" py={2}>
                Digite pelo menos 2 caracteres para buscar.
              </Typography>
            )}
          </>
        )}

        {/* Tipo de Vínculo */}
        {selectedTarefa && (
          <FormControl fullWidth margin="dense" size="small">
            <InputLabel>Tipo de Vínculo</InputLabel>
            <Select
              value={tipoVinculo}
              label="Tipo de Vínculo"
              onChange={(e) => setTipoVinculo(e.target.value)}
            >
              {tiposVinculo.map((tipo) => (
                <MenuItem key={tipo.value} value={tipo.value}>
                  {tipo.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary" disabled={loading}>
          Cancelar
        </Button>
        <Button
          onClick={handleVincular}
          color="primary"
          variant="contained"
          disabled={loading || !selectedTarefa}
          startIcon={loading ? <CircularProgress size={16} /> : <LinkIcon />}
        >
          {loading ? "Vinculando..." : "Vincular"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
